import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Theme system. The theme is chosen by the admin for everyone (app setting
 * `default_theme`); customers can't override it.
 *
 * It's applied by toggling classes on <html> (`.dark`, `.theme-mint`, …) that
 * flip the CSS-variable palette in index.css. "system" follows the OS via
 * prefers-color-scheme and updates live if the OS setting changes.
 *
 * The last theme received from the server is cached in localStorage purely so
 * the inline script in index.html can paint it before React mounts (no flash);
 * the server's value always replaces it once config loads.
 */

const STORAGE_KEY = 'hfb-theme';
// 'mint', 'sunrise' and 'pop' are alternate LIGHT palettes. 'system' follows
// the OS and only ever resolves to light/dark — never one of these.
//   sunrise — Sunrise Market: warm cream ground, light hero band, dark text
//   pop     — Mint Pop: mint ground, bold green hero band, light text
const THEMES = ['light', 'dark', 'mint', 'sunrise', 'pop', 'system'];

/** Palettes whose hero band is LIGHT, so chrome over it must be dark-on-light. */
export const LIGHT_BAND_THEMES = new Set(['mint', 'sunrise']);

const ThemeContext = createContext(null);

function prefersDark() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

/** Apply the resolved theme to <html> — the single place that touches the DOM. */
function applyResolved(resolved) {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.classList.toggle('theme-mint', resolved === 'mint');
  root.classList.toggle('theme-sunrise', resolved === 'sunrise');
  root.classList.toggle('theme-pop', resolved === 'pop');
  // Mint, Sunrise and Pop are light palettes; only 'dark' uses the dark hint.
  root.style.colorScheme = resolved === 'dark' ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);
  const [systemDark, setSystemDark] = useState(prefersDark);

  // Track the OS preference so "system" resolves live.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const resolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  // Reflect the resolved theme onto <html> whenever it changes.
  useEffect(() => {
    applyResolved(resolvedTheme);
  }, [resolvedTheme]);

  // Apply the admin's theme and cache it for the next cold start's first paint.
  const setDefaultTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage disabled — it still applies for this session */
    }
  }, []);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setDefaultTheme,
      isDark: resolvedTheme === 'dark',
      isMint: resolvedTheme === 'mint',
      isSunrise: resolvedTheme === 'sunrise',
      isPop: resolvedTheme === 'pop',
      // The hero band is light in Mint and Sunrise, dark in the rest.
      hasLightBand: LIGHT_BAND_THEMES.has(resolvedTheme),
    }),
    [theme, resolvedTheme, setDefaultTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return (
    useContext(ThemeContext) ?? {
      theme: 'system',
      resolvedTheme: prefersDark() ? 'dark' : 'light',
      setDefaultTheme: () => {},
      isDark: prefersDark(),
      isMint: false,
      isSunrise: false,
      isPop: false,
      hasLightBand: false,
    }
  );
}
